import Entity, { TEntity, TState } from '@/plugins/podolib/services/entities/Entity';
import { GetterTree } from 'vuex';
import {
  AnalysisType,
  AnalysisRunningMode,
  AnalysisWalkingMode,
} from '@/components/analysis/steps/type/AnalysisStepType';

export type TGraph = {
  category: string;
  type: string;
  subtype?: string;
};

export type TComment = {
  _id: string;
  date: string;
  doctorId: string;
  comment: string;
  author: {
    firstName: string;
    lastName: string;
  };
};

export type TAcquisition_ComputedData_Standard = {
  avg: number;
  std: number;
  valid?: boolean;
};

export type TAcquisition_ComputedData_Side_Aggregates = {
  [key: string]: any;
  swing_speed: TAcquisition_ComputedData_Standard;
  stance_time: TAcquisition_ComputedData_Standard;
  step_by_step_cadence: TAcquisition_ComputedData_Standard;
  stride_length: TAcquisition_ComputedData_Standard;
  walking_speed: TAcquisition_ComputedData_Standard;
  propulsion_speed: TAcquisition_ComputedData_Standard;
  pronation_angle_foot_in: TAcquisition_ComputedData_Standard;
  pronation_angle_FFI: TAcquisition_ComputedData_Standard;
  pronation_angle_FFO: TAcquisition_ComputedData_Standard;
  pronation_angle_foot_out: TAcquisition_ComputedData_Standard;
  gaitline_foot_in: TAcquisition_ComputedData_Standard;
  gaitline_FFO: TAcquisition_ComputedData_Standard;
  gaitline_FFI: TAcquisition_ComputedData_Standard;
  gaitline_foot_out: TAcquisition_ComputedData_Standard;
  swing_time: TAcquisition_ComputedData_Standard;
  flatfoot_time: TAcquisition_ComputedData_Standard;
  loading_time: TAcquisition_ComputedData_Standard;
  propulsion_time: TAcquisition_ComputedData_Standard;
  plantar_flexion_angle_foot_in?: TAcquisition_ComputedData_Standard;
  plantar_flexion_angle_foot_out?: TAcquisition_ComputedData_Standard;
  foot_progression_angle_FF?: TAcquisition_ComputedData_Standard;
  clearance?: TAcquisition_ComputedData_Standard;
  circumduction?: TAcquisition_ComputedData_Standard;
  braking_phase?: TAcquisition_ComputedData_Standard;
  impact_force?: TAcquisition_ComputedData_Standard;
  fore_foot?: boolean;
  k_leg?: TAcquisition_ComputedData_Standard;
  mid_foot?: boolean;
  percent_stance_time?: TAcquisition_ComputedData_Standard;
  percent_swing_time?: TAcquisition_ComputedData_Standard;
  percent_fore_foot?: TAcquisition_ComputedData_Standard;
  percent_mid_foot?: TAcquisition_ComputedData_Standard;
  percent_rear_foot?: TAcquisition_ComputedData_Standard;
  propulsive_phase?: TAcquisition_ComputedData_Standard;
  rear_foot?: boolean;
  running_speed?: TAcquisition_ComputedData_Standard;
  stride_height?: TAcquisition_ComputedData_Standard;
};
export type TAcquisition_ComputedData_Side = {
  [key: string]: any;
  aggregates: TAcquisition_ComputedData_Side_Aggregates;
  cadence_global: number;
  distance: number;
  step_count: number;
  steps: any[];
};

export type TAcquisition_ComputedData = {
  [key: string]: any;
  left: TAcquisition_ComputedData_Side;
  right: TAcquisition_ComputedData_Side;
};
export type TConditions = {
  [key: string]: any;
  _id: string;
  pains?: TConditionsPain[];
  shoes_type?: TConditionsShoes[];
  walking_aids?: string[];
};
export type TConditionsPain = {
  [key: string]: any;
  _id: string;
  level: string;
  location: string;
  side: string;
  zone_1: string;
  zone_2: string;
};
export type TConditionsShoes = {
  [key: string]: any;
  _id: string;
  cushioning: string;
  rigidity: string;
  wear: string;
};

/**
 * @deprecated use Type instead
 */
export enum TAnalysisType {
  Podosmart = 'podosmart',
  Vionic = 'vionic',
  Podorun = 'podorun',
}

export type TAcquisition = {
  [key: string]: any;
  _id: string;
  raw: string;
  doctorId: string;
  stationId: string;
  solesId: string;
  conditions: TConditions;
  computed_data: TAcquisition_ComputedData;
  data: {
    [key: string]: any;
    analysisMode: AnalysisRunningMode | AnalysisWalkingMode | null;
  };
  weight: number;
  height: number;
  bmi: object;
  footSize: number;
  patientAge: number;
  comments: any;
  analysisType: AnalysisType;
  updatedAt: string;
  createdAt: string;
};

export type TRecommendation = {
  podosmart: object;
  soles: object;
};

export type TInfos = {
  [key: string]: any;
  firstName: string;
  lastName: string;
  birthDate: string;
  height: number;
  weight: number;
  footSize: number;
  gender: number;
  address: {
    street_number: string;
    route: string;
    city: string;
    country: string;
    pos_code: string;
  };
  contact: {
    email: string;
    phone: string;
  };
  pathologies?: TPathologies[];
  customPathologies?: TCustomPathologies[];
  background?: string;
  consultationReason?: string;
};
type TCustomPathologies = {
  name: string;
  sinceDate?: string;
};
type TPathologies = {
  reference: TPathologiesReference;
  sinceDate?: string;
};
type TPathologiesReference = {
  name: {
    en?: string;
    fr?: string;
  };
  type: string;
  _id: string;
};

export type TPatient = {
  _id: string;
  acquisitions: TAcquisition[];
  infos: TInfos;
  demo?: boolean;
};

/**
 * @author Damien CORLER <d.corler@zhortech.com>
 * @since 1.0.0
 * @version 1.0.0
 */
export default class EntityPatients extends Entity<TPatient> {
  name: string = 'patients';

  public startListener() {
    this.Vue.prototype.$pdc.repository.socket.session.on(`${this.name} created`, (ressource: TEntity<TPatient>) => {
      // Commit
      this.store.commit(`pdc/entities/${this.name}/CREATE_ONE`, ressource);
      // Send global event
      this.Vue.prototype.$pdc.bus.emitDebug(`entities:${this.name}:created`, ressource);
      // Log
      this.logger.log(this.name, 'created');
      // Return ressource
      return ressource;
    });

    this.Vue.prototype.$pdc.repository.socket.session.on(
      `${this.name} patched`,
      async (ressource: TEntity<TPatient>) => {
        /*let storedPatient = this.Vue.prototype.$pdc.entities.patients.storedEntity.list.find(
        (x: TPatient) => x._id === ressource._id
      );

      console.log(storedPatient, storedPatient.infos.pathologies, ressource.infos.pathologies);

      if (storedPatient && storedPatient.infos.pathologies?.length < ressource.infos.pathologies?.length) {
        for (const pathology of ressource.infos.pathologies) {
          if (
            !storedPatient.infos.pathologies.findIndex((x: any) => {
              return x.reference._id === pathology.reference;
            })
          ) {
            try {
              const data = await this.Vue.prototype.$pdc
                .repository!.getRessource({
                identifier: pathology.reference,
                ressource: 'pathologies',
                params: {}
              });
              storedPatient.infos.pathologies.push({
                reference: data
              });
              ressource = storedPatient;
            }
            catch (e) {
              console.log(e);
            }
          }
        }
        console.log('storedPatient', storedPatient);
      }
      if (storedPatient && !storedPatient.infos.pathologies && ressource.infos.pathologies?.length) {
        console.log('Patient has not pathology');
        for (const pathology of ressource.infos.pathologies) {
          try {
            const data = await this.Vue.prototype.$pdc
              .repository!.getRessource({
              identifier: pathology.reference,
              ressource: 'pathologies',
              params: {}
            })
            storedPatient.infos = {
              ...storedPatient.infos,
              pathologies: [
                {
                  reference: data
                }
              ]
            };
            ressource = storedPatient;
          }
          catch (e) {
            console.log(e)
          }
        }
      }

      console.log(ressource);*/

        // Commit
        this.store.commit(`pdc/entities/${this.name}/PATCH_ONE`, ressource);
        // Send global event
        this.Vue.prototype.$pdc.bus.emitDebug(`entities:${this.name}:patched`, ressource);
        // Log
        this.logger.log(this.name, 'patched');
        // Return ressource
        return ressource;
      },
    );

    this.Vue.prototype.$pdc.repository.socket.session.on(`${this.name} removed`, (ressource: TEntity<TPatient>) => {
      // Commit
      this.store.commit(`pdc/entities/${this.name}/REMOVE`, ressource);
      // Send global event
      this.Vue.prototype.$pdc.bus.emitDebug(`entities:${this.name}:removed`, ressource);
      // Log
      this.logger.log(this.name, 'removed');
      // Return ressource
      return ressource;
    });

    this.extendListeners();
  }

  public async getDetails(_id: string): Promise<void> {
    this.store.commit(`pdc/entities/${this.name}/SET_LOADING`, true);
    // Start timer
    const t0 = performance.now();
    // Log
    console.log(`Entity "${this.name}" get details ...`);
    // Fetch ressource
    const result = await this.Vue.prototype.$pdc.repository
      .getRessource({
        identifier: _id,
        ressource: this.name,
        params: {
          $populate: {
            path: 'acquisitions',
            options: {
              sort: {
                createdAt: -1,
              },
            },
          },
        },
      })
      .catch(() => {
        this.store.commit(`pdc/entities/${this.name}/SET_LOADING`, false);
      });
    // Save result into the store
    if (result) {
      this.store.commit(`pdc/entities/${this.name}/PATCH_ONE`, result);
    }
    // Send event
    this.Vue.prototype.$pdc.bus.emit('entities:patients:get-details-completed', result);
    // Stop timer
    const t1 = performance.now();
    // Log
    console.info(`Get "${this.name}" details in ${Math.round(t1 - t0)}ms`);
    this.store.commit(`pdc/entities/${this.name}/SET_LOADING`, false);
    // Return result
    return result;
  }

  public get getters(): GetterTree<TState<TEntity<TPatient>>, any> {
    return {
      ...super.getters,
      ...{
        getAcquisitionWithId: (state) => (id: string, acquisitionId: string): TAcquisition | undefined => {
          const patient = state.list.find((x) => x._id === id);
          if (patient) {
            return patient.acquisitions.find((x) => x._id === acquisitionId);
          }
        },
      },
    };
  }

  /**
   * Helper to get an acquisiton with its ID from the store
   * @param id
   * @param acquisitionId
   */
  public getAcquisitionById(id: string, acquisitionId: string): TAcquisition {
    return this.store.getters[`pdc/entities/${this.name}/getAcquisitionWithId`](id, acquisitionId);
  }

  public async search(searchQuery: string, options?: { query?: any; replace?: boolean }) {
    // Default query
    let _query = {
      $select: ['infos', 'acquisitions.createdAt'],
      $limit: 8,
      $or: [
        {
          'infos.firstName': {
            $regex: `${searchQuery}`,
            $options: 'i',
          },
        },
        {
          'infos.lastName': {
            $regex: `${searchQuery}`,
            $options: 'i',
          },
        },
      ],
    };

    if (options?.replace && options?.query) {
      _query = options.query;
    } else {
      _query = {
        ..._query,
        ...options?.query,
      };
    }

    // Fetch ressource and return result
    return this.Vue.prototype.$pdc.repository.fetchRessource({
      ressource: this.name,
      params: _query,
    });
  }
}
